import { ComponentType, SVGProps } from 'react';
import { TechnologyDistributionAsset } from '../../services/technologyApi';
import {
  UtilizationBand,
  formatCpu,
  formatGb,
  utilizationBand,
  utilizationLabel,
} from './technologyDistributionView';

const BAR_CLASS: Record<UtilizationBand, string> = {
  unknown: 'bg-secondary-300',
  low: 'bg-emerald-500',
  moderate: 'bg-amber-400',
  high: 'bg-orange-500',
  critical: 'bg-rose-500',
};

const TILE_CLASS: Record<UtilizationBand, string> = {
  unknown: 'bg-secondary-50 text-secondary-700',
  low: 'bg-emerald-50 text-emerald-800',
  moderate: 'bg-amber-50 text-amber-800',
  high: 'bg-orange-50 text-orange-800',
  critical: 'bg-rose-50 text-rose-800',
};

type ResourceDefinition = {
  label: string;
  capacity: string;
  percent: number | null | undefined;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
};

type TechnologyDistributionResourceMeterProps = {
  asset: TechnologyDistributionAsset;
  variant: 'card' | 'panel';
  icons?: {
    cpu: ComponentType<SVGProps<SVGSVGElement>>;
    memory: ComponentType<SVGProps<SVGSVGElement>>;
    disk: ComponentType<SVGProps<SVGSVGElement>>;
  };
};

function resourcesOf(
  asset: TechnologyDistributionAsset,
  icons: TechnologyDistributionResourceMeterProps['icons'],
): ResourceDefinition[] {
  return [
    {
      label: 'CPU',
      capacity: formatCpu(asset.cpuCores),
      percent: asset.cpuUtilizationPercent,
      icon: icons?.cpu,
    },
    {
      label: 'Memória',
      capacity: formatGb(asset.ramGb),
      percent: asset.memoryUtilizationPercent,
      icon: icons?.memory,
    },
    {
      label: 'Disco',
      capacity: formatGb(asset.diskGb),
      percent: asset.diskUtilizationPercent,
      icon: icons?.disk,
    },
  ];
}

export function TechnologyDistributionResourceMeter({
  asset,
  variant,
  icons,
}: TechnologyDistributionResourceMeterProps) {
  const resources = resourcesOf(asset, icons);

  if (variant === 'panel') {
    return (
      <div className="grid grid-cols-3 gap-2">
        {resources.map((resource) => {
          const band = utilizationBand(resource.percent);
          const Icon = resource.icon;
          const capacityMissing = resource.capacity === 'Não informado';
          return (
            <div key={resource.label} className={`rounded-xl px-2 py-3 text-center ${TILE_CLASS[band]}`}>
              {Icon ? <Icon className="mx-auto h-4 w-4" aria-hidden="true" /> : null}
              <p className="mt-1 text-[11px] font-medium uppercase tracking-wide">{resource.label}</p>
              <p className={`mt-1 text-sm font-semibold leading-tight ${capacityMissing ? 'font-medium' : ''}`}>
                {resource.capacity}
              </p>
              <p className="mt-1 text-[11px] leading-tight opacity-80">{utilizationLabel(resource.percent)}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {resources.map((resource) => {
        const band = utilizationBand(resource.percent);
        const capacityMissing = resource.capacity === 'Não informado';
        const width = band === 'unknown' ? 0 : Math.max(0, Math.min(100, resource.percent ?? 0));
        return (
          <div key={resource.label} className="min-w-0">
            <p className="text-xs text-secondary-500">{resource.label}</p>
            <p className={`truncate text-sm font-semibold ${capacityMissing ? 'font-medium text-secondary-400' : 'text-secondary-900'}`}>
              {resource.capacity}
            </p>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary-100" aria-hidden="true">
              {width > 0 ? (
                <div className={`h-full rounded-full ${BAR_CLASS[band]}`} style={{ width: `${width}%` }} />
              ) : null}
            </div>
            <p className="mt-1 truncate text-[11px] text-secondary-500">{utilizationLabel(resource.percent)}</p>
          </div>
        );
      })}
    </div>
  );
}
